(() => {
  if (window.ElementNewsletter) return;

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

  const companyId = () => {
    const configured = String(window.theme?.settings?.klaviyoCompanyId || '').trim();
    if (configured) return configured;

    const scripts = document.querySelectorAll('script[src*="klaviyo"]');
    for (const script of scripts) {
      const src = String(script.src || '');
      try {
        const id = new URL(src, window.location.origin).searchParams.get('company_id');
        if (id) return id;
      } catch {
        // Keep scanning other script tags.
      }
      const query = src.match(/company_id=([A-Za-z0-9]+)/i);
      if (query) return query[1];
      const path = src.match(/static\.klaviyo\.com\/onsite\/js\/([A-Za-z0-9]+)\/klaviyo\.js/i);
      if (path) return path[1];
    }
    return 'RsHcMV';
  };

  const listId = () => String(window.theme?.settings?.klaviyoNewsletterListId || '').trim();

  const identify = (email, properties) => {
    const profile = { email, $email: email, ...properties };
    const klaviyo = window.klaviyo;
    if (klaviyo && typeof klaviyo.identify === 'function') {
      klaviyo.identify(profile);
      return;
    }
    (window.klaviyo = window.klaviyo || []).push(['identify', profile]);
  };

  const subscribeKlaviyo = async (email, tags) => {
    const id = companyId();
    const selectedList = listId();
    if (!selectedList) {
      throw new Error('Add the Email List ID in Theme settings → Klaviyo.');
    }

    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Element newsletter',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
                properties: {
                  source: 'Element newsletter',
                  newsletter_lists: tags,
                },
                subscriptions: {
                  email: {
                    marketing: {
                      consent: 'SUBSCRIBED',
                    },
                  },
                },
              },
            },
          },
        },
        relationships: {
          list: { data: { type: 'list', id: selectedList } },
        },
      },
    };

    const response = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(id)}`, {
      method: 'POST',
      headers: {
        accept: 'application/vnd.api+json',
        'content-type': 'application/vnd.api+json',
        revision: '2026-07-15',
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok && response.status !== 202) {
      throw new Error('Klaviyo could not subscribe this email.');
    }
    identify(email, { Accepts_Marketing: true });
  };

  const submitShopify = async (form) => {
    const action = form.getAttribute('action') || '/contact';
    const body = new FormData(form);
    try {
      await fetch(action, {
        method: 'POST',
        body,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
    } catch {
      // Shopify tags are secondary to Klaviyo consent.
    }
  };

  const selectedTags = (form) => {
    const hidden = form.querySelector('[data-newsletter-tags], [data-explore-subscribe-tags]');
    const chips = [...form.querySelectorAll('[data-newsletter-list], [data-explore-subscribe-list]')]
      .filter((input) => input.checked)
      .map((input) => input.value);
    const fromHidden = String(hidden?.value || '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);
    return [...new Set(['newsletter', ...fromHidden, ...chips])];
  };

  const syncTags = (form) => {
    const hidden = form.querySelector('[data-newsletter-tags], [data-explore-subscribe-tags]');
    if (!hidden) return;
    hidden.value = selectedTags(form).join(', ');
  };

  const emailFrom = (form) =>
    String(form.querySelector('input[type="email"]')?.value || '').trim().toLowerCase();

  const subscribe = async (form) => {
    syncTags(form);
    const email = emailFrom(form);
    if (!isValidEmail(email)) {
      throw new Error('Enter a valid email address.');
    }
    const tags = selectedTags(form);
    if (form.querySelector('[data-newsletter-list], [data-explore-subscribe-list]') && tags.filter((tag) => tag !== 'newsletter').length === 0) {
      throw new Error('Choose at least one list to join.');
    }
    const emailInput = form.querySelector('input[type="email"]');
    if (emailInput) emailInput.value = email;
    await subscribeKlaviyo(email, tags);
    await submitShopify(form);
  };

  const bindForm = (form, options = {}) => {
    if (!form || form.dataset.elementNewsletter === 'ready') return;
    form.dataset.elementNewsletter = 'ready';

    const lists = [...form.querySelectorAll('[data-newsletter-list], [data-explore-subscribe-list]')];
    lists.forEach((input) => {
      input.addEventListener('change', () => {
        input.closest('label')?.classList.toggle('is-selected', input.checked);
        syncTags(form);
      });
    });
    syncTags(form);

    const success = form.querySelector('.newsletter-form__success');
    const error = form.querySelector('.newsletter-form__error');
    const showError = (message) => {
      if (error) {
        error.hidden = false;
        error.textContent = message;
      }
      if (success) success.hidden = true;
    };
    const showSuccess = (message) => {
      if (success) {
        success.hidden = false;
        success.textContent = message;
      }
      if (error) error.hidden = true;
    };

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (form.dataset.elementNewsletter === 'busy' || form.classList.contains('is-success')) return;

      const submit = form.querySelector('[type="submit"]');
      form.dataset.elementNewsletter = 'busy';
      if (submit) submit.disabled = true;
      if (error) error.hidden = true;
      options.onStart?.();

      try {
        await subscribe(form);
        form.classList.add('is-success');
        form.closest('[data-explore-subscribe], newsletter-modal, .newsletter-modal')?.classList.add('is-success');
        showSuccess('You’re in. Welcome to Element.');
        options.onSuccess?.('You’re in. Welcome to Element.');
      } catch (errorObject) {
        const message = errorObject.message || 'Couldn’t join just now. Please try again.';
        showError(message);
        options.onError?.(message);
      } finally {
        form.dataset.elementNewsletter = 'ready';
        if (submit) submit.disabled = false;
      }
    });
  };

  window.ElementNewsletter = {
    isValidEmail,
    companyId,
    subscribe,
    bindForm,
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('form.newsletter-form, form.custom-explore__subscribe-form').forEach((form) => {
      if (form.dataset.elementNewsletterManual === 'true') return;
      bindForm(form);
    });
  });
})();
